import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 22. DISPONIBILITÉS
// Couche centrale Availability : disponibilité, indisponibilité,
// horaires, pause, congé, fermeture, réservation, blocage manuel.
// Le socle fournit les règles ; les modules métier (Zifek Calendar /
// Hospitality) appliquent l'exclusivité de réservation.
// ─────────────────────────────────────────────────────────────

export interface CoreAvailabilityRule {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  calendar_id: number | null;
  employee_id: number | null;
  entity_type: string | null;
  entity_id: string | null;
  rule_type: string;
  label: string | null;
  weekday: number | null;
  start_time: string | null;
  end_time: string | null;
  start_date: string | null;
  end_date: string | null;
  exclusive: boolean | null;
  status: string | null;
  created_at: string | null;
}

/** Types de règles couverts par la spec 22. */
export const AVAILABILITY_TYPES: {
  key: string;
  label: string;
  icon: string;
  className: string;
  /** Un créneau de ce type peut-il bloquer une réservation exclusive ? */
  blocking: boolean;
}[] = [
  { key: 'availability', label: 'Disponibilité', icon: 'ri-checkbox-circle-line', className: 'bg-primary-100 text-primary-900', blocking: false },
  { key: 'hours', label: 'Horaires', icon: 'ri-time-line', className: 'bg-primary-100 text-primary-900', blocking: false },
  { key: 'break', label: 'Pause', icon: 'ri-cup-line', className: 'bg-secondary-100 text-secondary-900', blocking: true },
  { key: 'reservation', label: 'Réservation', icon: 'ri-calendar-check-line', className: 'bg-accent-100 text-accent-900', blocking: true },
  { key: 'block', label: 'Blocage manuel', icon: 'ri-lock-line', className: 'bg-accent-100 text-accent-900', blocking: true },
  { key: 'unavailability', label: 'Indisponibilité', icon: 'ri-forbid-2-line', className: 'bg-foreground-100 text-foreground-700', blocking: true },
  { key: 'leave', label: 'Congé', icon: 'ri-flight-takeoff-line', className: 'bg-foreground-100 text-foreground-700', blocking: true },
  { key: 'closure', label: 'Fermeture', icon: 'ri-store-3-line', className: 'bg-foreground-100 text-foreground-700', blocking: true },
];

export function availabilityTypeMeta(key: string | null | undefined) {
  return AVAILABILITY_TYPES.find((t) => t.key === key) || AVAILABILITY_TYPES[0];
}

export const WEEKDAYS: { value: number; label: string; short: string }[] = [
  { value: 1, label: 'Lundi', short: 'Lun' },
  { value: 2, label: 'Mardi', short: 'Mar' },
  { value: 3, label: 'Mercredi', short: 'Mer' },
  { value: 4, label: 'Jeudi', short: 'Jeu' },
  { value: 5, label: 'Vendredi', short: 'Ven' },
  { value: 6, label: 'Samedi', short: 'Sam' },
  { value: 0, label: 'Dimanche', short: 'Dim' },
];

export function weekdayLabel(value: number | null | undefined): string {
  const d = WEEKDAYS.find((w) => w.value === value);
  return d ? d.label : '—';
}

/** Entités pouvant porter des règles de disponibilité. */
export const AVAILABILITY_ENTITIES: { key: string; label: string }[] = [
  { key: 'organization', label: 'Entreprise' },
  { key: 'employee', label: 'Employé' },
  { key: 'calendar', label: 'Calendrier' },
  { key: 'service', label: 'Service' },
  { key: 'resource', label: 'Ressource' },
];

export async function listCoreAvailabilityRules(organizationId: number): Promise<CoreAvailabilityRule[]> {
  const { data, error } = await supabase
    .from('core_availability_rules')
    .select('*')
    .eq('organization_id', organizationId)
    .order('rule_type', { ascending: true })
    .order('weekday', { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data || []) as CoreAvailabilityRule[];
}

export interface AvailabilityRuleInput {
  tenantId: number | null;
  organizationId: number;
  calendarId?: number | null;
  employeeId?: number | null;
  entityType?: string;
  entityId?: string | null;
  ruleType: string;
  label?: string | null;
  weekday?: number | null;
  startTime?: string | null;
  endTime?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  exclusive?: boolean;
  status?: string;
}

export async function createCoreAvailabilityRule(input: AvailabilityRuleInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_availability_rules')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      calendar_id: input.calendarId ?? null,
      employee_id: input.employeeId ?? null,
      entity_type: input.entityType || 'organization',
      entity_id: input.entityId || null,
      rule_type: input.ruleType,
      label: input.label || null,
      weekday: input.weekday ?? null,
      start_time: input.startTime || null,
      end_time: input.endTime || null,
      start_date: input.startDate || null,
      end_date: input.endDate || null,
      exclusive: input.exclusive ?? true,
      status: input.status || 'active',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreAvailabilityRule(id: number, input: Partial<AvailabilityRuleInput>): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (input.ruleType !== undefined) payload.rule_type = input.ruleType;
  if (input.label !== undefined) payload.label = input.label || null;
  if (input.weekday !== undefined) payload.weekday = input.weekday ?? null;
  if (input.startTime !== undefined) payload.start_time = input.startTime || null;
  if (input.endTime !== undefined) payload.end_time = input.endTime || null;
  if (input.startDate !== undefined) payload.start_date = input.startDate || null;
  if (input.endDate !== undefined) payload.end_date = input.endDate || null;
  if (input.exclusive !== undefined) payload.exclusive = input.exclusive;
  if (input.employeeId !== undefined) payload.employee_id = input.employeeId ?? null;
  if (input.status !== undefined) payload.status = input.status;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('core_availability_rules').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCoreAvailabilityRule(id: number): Promise<void> {
  const { error } = await supabase.from('core_availability_rules').delete().eq('id', id);
  if (error) throw error;
}

/** Convertit "09:00" en minutes depuis minuit. */
function toMinutes(time: string | null | undefined): number | null {
  if (!time) return null;
  const [h, m] = time.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h)) return null;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
}

/** Deux créneaux du même jour se chevauchent-ils ? */
export function timesOverlap(aStart: string | null, aEnd: string | null, bStart: string | null, bEnd: string | null): boolean {
  const a1 = toMinutes(aStart);
  const a2 = toMinutes(aEnd);
  const b1 = toMinutes(bStart);
  const b2 = toMinutes(bEnd);
  if (a1 == null || a2 == null || b1 == null || b2 == null) return false;
  return a1 < b2 && b1 < a2;
}

/**
 * Détecte un conflit entre une règle candidate et l'ensemble des règles existantes.
 * Un conflit survient lorsque deux règles bloquantes se chevauchent sur la même
 * cible (entité) et le même jour (plus les dates si fournies).
 * Utilisé pour empêcher les doubles réservations quand le module exige l'exclusivité.
 */
export function findAvailabilityConflict(
  candidate: Pick<CoreAvailabilityRule, 'rule_type' | 'weekday' | 'start_time' | 'end_time'>,
  rules: CoreAvailabilityRule[],
  excludeId?: number,
): CoreAvailabilityRule | null {
  if (!availabilityTypeMeta(candidate.rule_type).blocking) return null;
  for (const rule of rules) {
    if (rule.id === excludeId) continue;
    if (!availabilityTypeMeta(rule.rule_type).blocking) continue;
    if (rule.status && rule.status !== 'active') continue;
    if ((rule.weekday ?? null) !== (candidate.weekday ?? null)) continue;
    if (timesOverlap(candidate.start_time, candidate.end_time, rule.start_time, rule.end_time)) {
      return rule;
    }
  }
  return null;
}